//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { navigateTo } from '../../../utils/navigation';
import {
  Renew,
  TrashCan,
  Copy,
  ViewOff,
  WarningAlt,
  Information,
  ChevronDown,
  ChevronUp,
  Login,
  DocumentExport,
} from '@carbon/icons-react';
import { useUserAuth } from '../../../UserAuth';
import { useConfig } from '../../../Config';
import WebhookDataService, {
  Webhook,
  WebhookEventSummary,
} from '../../../utils/WebhookDataService';
import { Button } from '../../ui/button';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '../../ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../ui/tooltip';
import { Dialog, DialogContent } from '../../ui/dialog';
import { Switch } from '../../ui/switch';
import CustomSnackbar, { useSnackbar } from '../../common/CustomSnackbar';
import AddWebhookDialog from './AddWebhookDialog';
import { consumePendingWebhookCreatedToast } from './webhookCreatedToast';

interface WebhooksTabProps {
  loopId: string;
  teamspaceId: string;
}

type DeliveryTone =
  | 'success'
  | 'failed'
  | 'processing'
  | 'rejected'
  | 'blocked';

interface DeliveryResult {
  code: string;
  labelKey: string;
  tone: DeliveryTone;
}

// Phase 2 — deliveries rejected/blocked at ingestion carry an explicit reason.
// "Rejected" = the sender's fault (signature, timestamp); "Blocked" = the
// webhook's config (disabled, no OAuth, no task).
const REJECTED_REASONS = new Set([
  'signature_failed',
  'disabled',
  'timestamp_expired',
]);
const BLOCKED_REASONS = new Set(['oauth_not_completed', 'task_not_configured']);

/**
 * Derive the user-facing status code + result badge from a delivery. Prefers the
 * explicit ingestion outcome (Phase 2); falls back to the processing lifecycle
 * status when no outcome was recorded (Phase 1 rows).
 */
const deriveDeliveryResult = (
  status?: string | null,
  hasError?: boolean,
  httpStatus?: number | null,
  outcomeReason?: string | null,
): DeliveryResult => {
  if (outcomeReason && REJECTED_REASONS.has(outcomeReason)) {
    return {
      code: httpStatus ? String(httpStatus) : '401',
      labelKey: 'webhook-history-result-rejected',
      tone: 'rejected',
    };
  }
  if (outcomeReason && BLOCKED_REASONS.has(outcomeReason)) {
    return {
      code: httpStatus ? String(httpStatus) : '400',
      labelKey: 'webhook-history-result-blocked',
      tone: 'blocked',
    };
  }
  if (status === 'completed' && !hasError) {
    return {
      code: '200',
      labelKey: 'webhook-history-result-success',
      tone: 'success',
    };
  }
  if (status === 'failed' || hasError) {
    return {
      code: httpStatus ? String(httpStatus) : '500',
      labelKey: 'webhook-history-result-failed',
      tone: 'failed',
    };
  }
  return {
    code: '202',
    labelKey: 'webhook-history-result-processing',
    tone: 'processing',
  };
};

const DELIVERY_TONE_CLASSES: Record<DeliveryTone, string> = {
  success:
    'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  processing:
    'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  rejected: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  blocked:
    'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
};

// Humanized labels for a rejected/blocked delivery's outcome reason — shown as a
// muted subtitle in the row so a failure is self-explanatory without a detail view.
const REASON_LABEL_KEYS: Record<string, string> = {
  signature_failed: 'webhook-reason-signature-failed',
  timestamp_expired: 'webhook-reason-timestamp-expired',
  disabled: 'webhook-reason-disabled',
  oauth_not_completed: 'webhook-reason-oauth-incomplete',
  task_not_configured: 'webhook-reason-task-not-configured',
};

const WebhooksTab: React.FC<WebhooksTabProps> = ({ loopId, teamspaceId }) => {
  const { t } = useTranslation();
  const { user, isAuthenticated } = useUserAuth();
  const { services, oidc } = useConfig();

  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [revealedKeys, setRevealedKeys] = useState<Record<string, boolean>>({});
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [webhookToDelete, setWebhookToDelete] = useState<string | null>(null);
  const [isVerifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [webhookToDisable, setWebhookToDisable] = useState<string | null>(null);
  // Delivery history (read-only traceability) — inline per-webhook expander
  const [historyWebhookId, setHistoryWebhookId] = useState<string | null>(null);
  const [events, setEvents] = useState<WebhookEventSummary[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const { snackbarState, showSnackbar, closeSnackbar } = useSnackbar();

  const userRef = useRef(user);
  const isAuthenticatedRef = useRef(isAuthenticated);

  useEffect(() => {
    userRef.current = user;
    isAuthenticatedRef.current = isAuthenticated;
  }, [user, isAuthenticated]);

  const webhookService = useMemo(
    () =>
      new WebhookDataService(
        () => userRef.current,
        () => isAuthenticatedRef.current,
        services?.analyticsai?.url,
      ),
    [services?.analyticsai?.url],
  );

  const fetchWebhooks = useCallback(async () => {
    if (!loopId || !teamspaceId) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await webhookService.listWebhooks(loopId, teamspaceId);
      setWebhooks(data);
    } catch {
      setError(t('webhook-fetch-error'));
    } finally {
      setIsLoading(false);
    }
  }, [webhookService, loopId, teamspaceId, t]);

  useEffect(() => {
    fetchWebhooks();
  }, [fetchWebhooks]);

  // Show the "webhook created" toast left behind by AddWebhookDialog when
  // creation required an OAuth redirect (a full-page navigation that wipes
  // React state, so the toast can't be shown before leaving).
  useEffect(() => {
    const pendingMessage = consumePendingWebhookCreatedToast();
    if (pendingMessage) {
      showSnackbar(pendingMessage, 'success');
    }
  }, [showSnackbar]);

  const handleCopyUrl = useCallback(
    async (url: string) => {
      try {
        await navigator.clipboard.writeText(url);
        showSnackbar(t('webhook-url-copied'), 'success');
      } catch {
        showSnackbar(t('webhook-copy-failed'), 'error');
      }
    },
    [showSnackbar, t],
  );

  const handleCopyKey = useCallback(
    async (key: string) => {
      try {
        await navigator.clipboard.writeText(key);
        showSnackbar(t('webhook-key-copied'), 'success');
      } catch {
        showSnackbar(t('webhook-copy-failed'), 'error');
      }
    },
    [showSnackbar, t],
  );

  const toggleKeyVisibility = useCallback((webhookId: string) => {
    setRevealedKeys((prev) => ({
      ...prev,
      [webhookId]: !prev[webhookId],
    }));
  }, []);

  const handleRegenerateKey = useCallback(
    async (webhookId: string) => {
      setActionInProgress(webhookId);
      try {
        await webhookService.regenerateKey(loopId, webhookId, teamspaceId);
        showSnackbar(t('webhook-key-regenerated'), 'success');
        fetchWebhooks();
      } catch {
        showSnackbar(t('webhook-key-regenerate-failed'), 'error');
      } finally {
        setActionInProgress(null);
      }
    },
    [webhookService, loopId, fetchWebhooks, showSnackbar, t],
  );

  const handleDelete = useCallback(
    async (webhookId: string) => {
      setActionInProgress(webhookId);
      try {
        await webhookService.deleteWebhook(loopId, webhookId, teamspaceId);
        showSnackbar(t('webhook-deleted'), 'success');
        setDeleteDialogOpen(false);
        setWebhookToDelete(null);
        fetchWebhooks();
      } catch {
        showSnackbar(t('webhook-delete-failed'), 'error');
      } finally {
        setActionInProgress(null);
      }
    },
    [webhookService, loopId, fetchWebhooks, showSnackbar, t],
  );

  const handleAuthorize = useCallback(
    async (webhookId: string) => {
      setActionInProgress(webhookId);
      try {
        const resp = await webhookService.oauthInitiate(
          loopId,
          webhookId,
          teamspaceId,
        );
        if (resp?.oauth_url) {
          // Redirect to Keycloak; it returns to the webhook list after consent.
          const allowedOrigin = new URL(oidc.authorization).origin;
          const redirectUrl = new URL(resp.oauth_url);
          if (redirectUrl.origin !== allowedOrigin) {
            throw new Error('Unexpected OAuth redirect origin');
          }
          navigateTo(resp.oauth_url);
          return;
        }
        showSnackbar(t('webhook-authorize-failed'), 'error');
      } catch {
        showSnackbar(t('webhook-authorize-failed'), 'error');
      } finally {
        setActionInProgress(null);
      }
    },
    [webhookService, loopId, teamspaceId, showSnackbar, t],
  );

  const handleSetVerification = useCallback(
    async (webhookId: string, mode: 'hmac' | 'none') => {
      setActionInProgress(webhookId);
      try {
        await webhookService.setVerificationMode(
          loopId,
          webhookId,
          teamspaceId,
          mode,
        );
        showSnackbar(
          mode === 'none'
            ? t('webhook-hmac-disabled')
            : t('webhook-hmac-enabled'),
          'success',
        );
        setVerifyDialogOpen(false);
        setWebhookToDisable(null);
        fetchWebhooks();
      } catch {
        showSnackbar(t('webhook-hmac-update-failed'), 'error');
      } finally {
        setActionInProgress(null);
      }
    },
    [webhookService, loopId, teamspaceId, fetchWebhooks, showSnackbar, t],
  );

  const openHistory = useCallback(
    async (webhookId: string) => {
      // Clicking the Output icon of the already-open webhook closes the expander.
      if (historyWebhookId === webhookId) {
        setHistoryWebhookId(null);
        setEvents([]);
        setEventsError(null);
        return;
      }
      setHistoryWebhookId(webhookId);
      setEvents([]);
      setEventsError(null);
      setEventsLoading(true);
      try {
        const data = await webhookService.listWebhookEvents(
          loopId,
          webhookId,
          teamspaceId,
        );
        setEvents(data);
      } catch {
        setEventsError(t('webhook-history-error'));
      } finally {
        setEventsLoading(false);
      }
    },
    [historyWebhookId, webhookService, loopId, teamspaceId, t],
  );

  const formatDateTime = (dateStr?: string | null) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleString('en-US');
    } catch {
      return dateStr;
    }
  };

  const channelLabel = useCallback(
    (channel?: string | null) => {
      switch (channel) {
        case 'slack':
          return t('webhook-channel-slack');
        case 'google_chat':
          return t('webhook-channel-google-chat');
        case 'teams':
          return t('webhook-channel-teams');
        default:
          return '—';
      }
    },
    [t],
  );

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div id="id-webhooks-tab" data-testid="webhooks-tab" className="mt-6">
      {/* Snackbar for success/error messages */}
      <CustomSnackbar
        open={snackbarState.open}
        message={snackbarState.message}
        severity={snackbarState.severity}
        onClose={closeSnackbar}
      />

      {/* Header with Add button */}
      <div
        id="id-webhooks-header"
        data-testid="webhooks-header"
        className="mb-4 flex items-center justify-between"
      >
        <h3
          id="id-webhooks-section-title"
          data-testid="webhooks-section-title"
          className="text-base font-semibold text-foreground"
        >
          {t('webhooks')}
        </h3>
        <Button
          data-testid="add-webhook-button"
          onClick={() => setAddDialogOpen(true)}
          className="flex items-center gap-2 rounded bg-primary px-4 py-2 text-sm text-primary-foreground"
        >
          {t('webhook-add-button')}
        </Button>
      </div>

      {/* Delete confirmation dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="pt-6">
          <div
            className="flex items-start gap-4"
            id="id-webhook-delete-dialog-content"
            data-testid="webhook-delete-dialog-content"
          >
            <WarningAlt size={20} className="mt-1 text-[#f52929]" />
            <div
              id="id-webhook-delete-dialog-text"
              data-testid="webhook-delete-dialog-text"
            >
              <h3 className="font-semibold text-gray-900 dark:text-white">
                {t('webhook-confirm-deletion')}
              </h3>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                {t('webhook-delete-confirm')}
              </p>
            </div>
          </div>
          <div
            className="mt-6 flex justify-end gap-2"
            id="id-webhook-delete-dialog-actions"
            data-testid="webhook-delete-dialog-actions"
          >
            <Button
              onClick={() => {
                setDeleteDialogOpen(false);
                setWebhookToDelete(null);
              }}
              variant="outline"
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={() => {
                if (webhookToDelete) {
                  handleDelete(webhookToDelete);
                }
              }}
              disabled={
                !webhookToDelete || actionInProgress === webhookToDelete
              }
              className="bg-[#f52929] text-white hover:bg-[#da1e28]"
            >
              {t('yes-delete') || 'Yes, Delete'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Disable-HMAC confirmation dialog */}
      <Dialog open={isVerifyDialogOpen} onOpenChange={setVerifyDialogOpen}>
        <DialogContent className="pt-6">
          <div
            className="flex items-start gap-4"
            id="id-webhook-disable-hmac-dialog-content"
            data-testid="webhook-disable-hmac-dialog-content"
          >
            <WarningAlt size={20} className="mt-1 text-[#f52929]" />
            <div
              id="id-webhook-disable-hmac-dialog-text"
              data-testid="webhook-disable-hmac-dialog-text"
            >
              <h3 className="font-semibold text-gray-900 dark:text-white">
                {t('webhook-confirm-disable-hmac')}
              </h3>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                {t('webhook-disable-hmac-confirm')}
              </p>
            </div>
          </div>
          <div
            className="mt-6 flex justify-end gap-2"
            id="id-webhook-disable-hmac-dialog-actions"
            data-testid="webhook-disable-hmac-dialog-actions"
          >
            <Button
              onClick={() => {
                setVerifyDialogOpen(false);
                setWebhookToDisable(null);
              }}
              variant="outline"
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={() => {
                if (webhookToDisable) {
                  handleSetVerification(webhookToDisable, 'none');
                }
              }}
              disabled={
                !webhookToDisable || actionInProgress === webhookToDisable
              }
              className="bg-[#f52929] text-white hover:bg-[#da1e28]"
            >
              {t('webhook-yes-disable') || 'Yes, Disable'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Table */}
      {isLoading ? (
        <div
          data-testid="webhooks-loading"
          className="flex justify-center py-12"
        >
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      ) : error ? (
        <div
          data-testid="webhooks-error"
          role="alert"
          className="rounded-md bg-red-50 p-4 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-400"
        >
          {error}
        </div>
      ) : webhooks.length === 0 ? (
        <div
          data-testid="webhooks-empty"
          className="flex flex-col items-center justify-center py-12 text-muted-foreground"
        >
          <p className="text-sm">{t('webhook-empty-state')}</p>
        </div>
      ) : (
        <TooltipProvider>
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/50">
                <TableHead>{t('webhook-col-hook')}</TableHead>
                <TableHead>{t('name')}</TableHead>
                <TableHead>{t('webhook-col-task')}</TableHead>
                <TableHead>{t('webhook-col-channel')}</TableHead>
                <TableHead>{t('webhook-col-hmac-key')}</TableHead>
                <TableHead>{t('webhook-col-output')}</TableHead>
                <TableHead>{t('webhook-col-status')}</TableHead>
                <TableHead>{t('webhook-col-date-added')}</TableHead>
                <TableHead className="text-right">
                  {t('webhook-col-actions')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {webhooks.map((webhook) => (
                <React.Fragment key={webhook.id}>
                  <TableRow data-testid={`webhook-row-${webhook.id}`}>
                    {/* Hook URL */}
                    <TableCell className="max-w-[280px]">
                      <div className="flex items-center gap-1.5">
                        <span
                          className="truncate font-mono text-xs text-muted-foreground"
                          title={webhook.webhook_url}
                        >
                          {webhook.webhook_url}
                        </span>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => handleCopyUrl(webhook.webhook_url)}
                              className="shrink-0 rounded p-0.5 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                              aria-label={t('webhook-copy-url')}
                            >
                              <Copy size={14} />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            {t('webhook-copy-url')}
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </TableCell>

                    {/* Name */}
                    <TableCell>
                      <span className="text-sm font-medium text-foreground">
                        {webhook.name}
                      </span>
                    </TableCell>

                    {/* Task */}
                    <TableCell className="max-w-[220px]">
                      <span
                        className="block truncate text-sm text-muted-foreground"
                        title={webhook.task_name || ''}
                      >
                        {webhook.task_name || '—'}
                      </span>
                    </TableCell>

                    {/* Channel — Slack webhooks also show the target channel */}
                    <TableCell>
                      <span
                        className="text-sm text-muted-foreground"
                        data-testid={`webhook-channel-${webhook.id}`}
                      >
                        {channelLabel(webhook.output_channel_type)}
                        {webhook.output_channel_type === 'slack' &&
                          (webhook.slack_channel_name ||
                            webhook.slack_channel_id) && (
                            <>
                              {' · '}
                              <span className="font-mono text-xs">
                                #
                                {webhook.slack_channel_name ||
                                  webhook.slack_channel_id}
                              </span>
                            </>
                          )}
                      </span>
                    </TableCell>

                    {/* HMAC Key */}
                    <TableCell className="max-w-[220px]">
                      {webhook.verification_mode === 'none' ? (
                        <span
                          data-testid={`webhook-hmac-unsigned-${webhook.id}`}
                          className="inline-flex items-center gap-1 text-xs italic text-muted-foreground"
                        >
                          <WarningAlt size={12} aria-hidden="true" />
                          {t('webhook-hmac-unsigned')}
                        </span>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <span className="truncate font-mono text-xs text-muted-foreground">
                            {revealedKeys[webhook.id]
                              ? webhook.hmac_key
                              : webhook.hmac_key_masked ||
                                '••••••••••••••••••••'}
                          </span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                onClick={() => handleCopyKey(webhook.hmac_key)}
                                className="shrink-0 rounded p-0.5 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                aria-label={t('webhook-copy-key')}
                              >
                                <Copy size={14} />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {t('webhook-copy-key')}
                            </TooltipContent>
                          </Tooltip>
                          <button
                            onClick={() => toggleKeyVisibility(webhook.id)}
                            className="shrink-0 rounded p-0.5 text-sm font-medium text-[#0F62FE] transition-colors hover:text-[#0043CE] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            aria-label={
                              revealedKeys[webhook.id]
                                ? t('webhook-hide-key')
                                : t('webhook-show-key')
                            }
                          >
                            {revealedKeys[webhook.id] ? (
                              <ViewOff size={14} />
                            ) : (
                              <span className="text-xs">{t('show')}</span>
                            )}
                          </button>
                        </div>
                      )}
                    </TableCell>

                    {/* Output — opens the delivery history for this webhook */}
                    <TableCell>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => openHistory(webhook.id)}
                            className={`rounded p-1.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                              historyWebhookId === webhook.id
                                ? 'bg-secondary text-foreground'
                                : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                            }`}
                            aria-label={t('webhook-output-history')}
                            data-testid={`webhook-output-${webhook.id}`}
                            aria-expanded={historyWebhookId === webhook.id}
                          >
                            <DocumentExport size={16} />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          {t('webhook-output-history')}
                        </TooltipContent>
                      </Tooltip>
                    </TableCell>

                    {/* Status */}
                    <TableCell>
                      {webhook.oauth_completed ? (
                        <span
                          data-testid={`webhook-status-active-${webhook.id}`}
                          className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/30 dark:text-green-400"
                        >
                          {t('webhook-status-active')}
                        </span>
                      ) : (
                        <span
                          data-testid={`webhook-status-auth-required-${webhook.id}`}
                          className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-400"
                        >
                          <WarningAlt size={12} aria-hidden="true" />
                          {t('webhook-status-auth-required')}
                        </span>
                      )}
                    </TableCell>

                    {/* Date Added */}
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {formatDate(webhook.created_at)}
                      </span>
                    </TableCell>

                    {/* Actions */}
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {/* HMAC enable/disable toggle */}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex items-center">
                              <Switch
                                size="sm"
                                checked={
                                  (webhook.verification_mode ?? 'hmac') ===
                                  'hmac'
                                }
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    handleSetVerification(webhook.id, 'hmac');
                                  } else {
                                    setWebhookToDisable(webhook.id);
                                    setVerifyDialogOpen(true);
                                  }
                                }}
                                disabled={actionInProgress === webhook.id}
                                aria-label={t('webhook-hmac-toggle')}
                                data-testid={`webhook-hmac-toggle-${webhook.id}`}
                              />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {webhook.verification_mode === 'none'
                              ? t('webhook-hmac-toggle-off')
                              : t('webhook-hmac-toggle-on')}
                          </TooltipContent>
                        </Tooltip>
                        {!webhook.oauth_completed && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                onClick={() => handleAuthorize(webhook.id)}
                                disabled={actionInProgress === webhook.id}
                                data-testid={`webhook-authorize-${webhook.id}`}
                                className="rounded p-1.5 text-amber-600 transition-colors hover:bg-amber-50 hover:text-amber-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 dark:hover:bg-amber-900/20"
                                aria-label={t('webhook-authorize')}
                              >
                                <Login size={16} />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {t('webhook-authorize')}
                            </TooltipContent>
                          </Tooltip>
                        )}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => handleRegenerateKey(webhook.id)}
                              disabled={actionInProgress === webhook.id}
                              className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                              aria-label={t('webhook-regenerate-key')}
                            >
                              <Renew size={16} />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            {t('webhook-regenerate-key')}
                          </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => {
                                setWebhookToDelete(webhook.id);
                                setDeleteDialogOpen(true);
                              }}
                              disabled={actionInProgress === webhook.id}
                              className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-red-50 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 dark:hover:bg-red-900/20"
                              aria-label={t('webhook-delete')}
                            >
                              <TrashCan size={16} />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>{t('webhook-delete')}</TooltipContent>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                  {historyWebhookId === webhook.id && (
                    <TableRow data-testid="webhook-history-panel">
                      {/* colSpan is clamped by the browser to the real column
                          count, so a large value spans the row even if columns
                          are added/removed later. */}
                      <TableCell colSpan={99} className="bg-secondary/20 p-0">
                        <div className="p-4">
                          {eventsLoading ? (
                            <div
                              className="py-4 text-center text-sm text-muted-foreground"
                              data-testid="webhook-history-loading"
                            >
                              {t('webhook-history-loading')}
                            </div>
                          ) : eventsError ? (
                            <div
                              className="py-4 text-center text-sm text-destructive"
                              data-testid="webhook-history-error"
                            >
                              {eventsError}
                            </div>
                          ) : events.length === 0 ? (
                            <div
                              className="rounded-lg border border-dashed border-border py-6 text-center text-sm text-muted-foreground"
                              data-testid="webhook-history-empty"
                            >
                              {t('webhook-history-empty')}
                            </div>
                          ) : (
                            <div className="overflow-hidden rounded-lg border border-border bg-background">
                              <Table>
                                <TableHeader>
                                  <TableRow className="bg-secondary/50">
                                    <TableHead>
                                      {t('webhook-history-col-date')}
                                    </TableHead>
                                    <TableHead>
                                      {t('webhook-history-col-event')}
                                    </TableHead>
                                    <TableHead>
                                      {t('webhook-history-col-task')}
                                    </TableHead>
                                    <TableHead>
                                      {t('webhook-history-col-status')}
                                    </TableHead>
                                    <TableHead>
                                      {t('webhook-history-col-result')}
                                    </TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {events.map((ev) => {
                                    const result = deriveDeliveryResult(
                                      ev.status,
                                      ev.has_error,
                                      ev.http_status,
                                      ev.outcome_reason,
                                    );
                                    const reasonKey = ev.outcome_reason
                                      ? REASON_LABEL_KEYS[ev.outcome_reason]
                                      : undefined;
                                    return (
                                      <TableRow
                                        key={ev.webhook_event_id}
                                        data-testid={`webhook-history-row-${ev.webhook_event_id}`}
                                      >
                                        <TableCell>
                                          <span className="whitespace-nowrap text-sm text-muted-foreground">
                                            {formatDateTime(ev.received_at)}
                                          </span>
                                        </TableCell>
                                        <TableCell>
                                          <span className="font-mono text-xs text-foreground">
                                            {ev.event_type || '—'}
                                          </span>
                                          {(reasonKey || ev.outcome_reason) && (
                                            <span className="mt-0.5 block text-xs text-muted-foreground">
                                              {reasonKey
                                                ? t(reasonKey)
                                                : ev.outcome_reason}
                                            </span>
                                          )}
                                        </TableCell>
                                        <TableCell className="max-w-[220px]">
                                          <span
                                            className="block truncate text-sm text-muted-foreground"
                                            title={ev.task_name || ''}
                                          >
                                            {ev.task_name || '—'}
                                          </span>
                                        </TableCell>
                                        <TableCell>
                                          <span className="font-mono text-xs text-muted-foreground">
                                            {result.code}
                                          </span>
                                        </TableCell>
                                        <TableCell>
                                          <span
                                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${DELIVERY_TONE_CLASSES[result.tone]}`}
                                          >
                                            {t(result.labelKey)}
                                          </span>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </div>
                          )}
                          {!eventsLoading &&
                            !eventsError &&
                            events.length > 0 && (
                              <p className="mt-3 text-xs text-muted-foreground">
                                {t('webhook-history-hint')}
                              </p>
                            )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TooltipProvider>
      )}

      {/* Add Webhook Dialog */}
      <AddWebhookDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        webhookService={webhookService}
        loopId={loopId}
        teamspaceId={teamspaceId}
        existingWebhooks={webhooks}
        onWebhookCreated={fetchWebhooks}
        onError={(msg) => showSnackbar(msg, 'error')}
        onSuccess={(msg) => showSnackbar(msg, 'success')}
      />
    </div>
  );
};

export default WebhooksTab;
